/* ==========================================================================
   Exact BC Pro Shop Trajectory Physics Engine (Rotated Vertically for Mobile)
   ========================================================================== */

import { appState } from './state.js';

const DEFAULT_PINS = [
  { id: 1, board: 20, dist: 60, hit: false },
  { id: 2, board: 24, dist: 61, hit: false },
  { id: 3, board: 16, dist: 61, hit: false },
  { id: 4, board: 28, dist: 62, hit: false },
  { id: 5, board: 20, dist: 62, hit: false },
  { id: 6, board: 12, dist: 62, hit: false },
  { id: 7, board: 32, dist: 63, hit: false },
  { id: 8, board: 24, dist: 63, hit: false },
  { id: 9, board: 16, dist: 63, hit: false },
  { id: 10, board: 8, dist: 63, hit: false }
];

export function initVisualizer() {
  const canvas = document.getElementById('laneCanvas');
  if (!canvas) return;

  const ctx = canvas.getContext('2d');

  // Controls
  const oilRadios = document.querySelectorAll('input[name="oilCondition"]');
  const standSlider = document.getElementById('visStandBoard');
  const targetSlider = document.getElementById('visTargetBoard');
  const speedSlider = document.getElementById('visSpeed');
  const revSlider = document.getElementById('visRevRate');
  const drillingSelect = document.getElementById('visDrillingSelect');
  const ballSelect = document.getElementById('visBallSelect');
  const compareToggle = document.getElementById('visCompareToggle');
  const compareGroup = document.getElementById('compareBallGroup');
  const ballSelect2 = document.getElementById('visBallSelect2');
  const btnRunSim = document.getElementById('btnRunSim');

  // Vertical Canvas Logical Resolution: 380px wide × 560px high
  const CANV_W = 380;
  const CANV_H = 560;

  const GUTTER_MARGIN = 35;
  const LANE_WIDTH = CANV_W - GUTTER_MARGIN * 2; // 310px
  const BOARD_W = LANE_WIDTH / 40;

  const FOUL_LINE_Y = 510;
  const ARROWS_Y = 380;
  const PIN_DECK_Y = 50;

  let activePins = JSON.parse(JSON.stringify(DEFAULT_PINS));
  let isDraggingStand = false;
  let isDraggingTarget = false;
  let simIsRunning = false;
  let simInterval = null;
  let currentFrame = 0;
  let simPath1 = [];
  let simPath2 = [];

  function boardToCanvasX(board) {
    // Board 1 is right gutter, Board 40 is left gutter
    return GUTTER_MARGIN + (40 - board) * BOARD_W;
  }

  function canvasXToBoard(x) {
    const b = 40 - ((x - GUTTER_MARGIN) / BOARD_W);
    return Math.min(39, Math.max(1, Math.round(b * 2) / 2));
  }

  function distanceToCanvasY(xDist) {
    // xDist in BC Pro Shop math goes from 15 (foul line) to 740 (pin deck)
    const ratio = Math.max(0, Math.min(1, (xDist - 15) / (740 - 15)));
    return FOUL_LINE_Y - ratio * (FOUL_LINE_Y - PIN_DECK_Y);
  }

  function resizeCanvas() {
    const container = canvas.parentElement;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const width = Math.floor(rect.width || 360);
    const height = Math.floor(rect.height || 520);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const targetWidth = Math.max(width, 300) * dpr;
    const targetHeight = Math.max(height, 400) * dpr;

    if (canvas.width === targetWidth && canvas.height === targetHeight) {
      return; // Dimensions already match: prevents infinite resize loop!
    }

    canvas.width = targetWidth;
    canvas.height = targetHeight;
    drawFrame();
  }

  let resizeTimer = null;
  window.addEventListener('resize', () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(resizeCanvas, 50);
  });

  if (window.ResizeObserver && canvas.parentElement) {
    const ro = new ResizeObserver(() => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(resizeCanvas, 50);
    });
    ro.observe(canvas.parentElement);
  }

  function populateBallDropdowns() {
    const s = appState.get();
    if (!ballSelect) return;

    ballSelect.innerHTML = '';
    if (ballSelect2) ballSelect2.innerHTML = '';

    s.arsenal.forEach(ball => {
      const opt1 = document.createElement('option');
      opt1.value = ball.id;
      opt1.textContent = `${ball.brand} ${ball.name} (${ball.coverType})`;
      if (ball.id === s.activeBallId) opt1.selected = true;
      ballSelect.appendChild(opt1);

      if (ballSelect2) {
        const opt2 = document.createElement('option');
        opt2.value = ball.id;
        opt2.textContent = `${ball.brand} ${ball.name} (${ball.coverType})`;
        if (ball.id === s.compareBallId) opt2.selected = true;
        ballSelect2.appendChild(opt2);
      }
    });
  }

  function updateControlsFromState() {
    const s = appState.get();
    
    if (standSlider) standSlider.value = s.standBoard;
    if (targetSlider) targetSlider.value = s.targetBoard;
    if (speedSlider) speedSlider.value = s.speed || 16.5;
    if (revSlider) revSlider.value = s.revRate || 350;
    if (drillingSelect) drillingSelect.value = s.drilling || 'benchmark';
    if (compareToggle) compareToggle.checked = s.compareMode;

    document.getElementById('visStandVal').textContent = s.standBoard;
    document.getElementById('visTargetVal').textContent = s.targetBoard;
    document.getElementById('visSpeedVal').textContent = (s.speed || 16.5) + ' MPH';
    document.getElementById('visRevVal').textContent = (s.revRate || 350) + ' RPM';

    if (compareGroup) compareGroup.classList.toggle('hidden', !s.compareMode);

    document.querySelectorAll('.oil-radio').forEach(label => {
      const radio = label.querySelector('input');
      if (radio && radio.value === s.activeOilCondition) {
        radio.checked = true;
        label.classList.add('active');
      } else {
        label.classList.remove('active');
      }
    });

    populateBallDropdowns();
  }

  // --- Drag Handling Logic for Vertical Lane ---
  function getCanvasCoords(e) {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const scaleX = CANV_W / rect.width;
    const scaleY = CANV_H / rect.height;

    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    return { x, y };
  }

  function handlePointerDown(e) {
    const { x, y } = getCanvasCoords(e);
    const s = appState.get();

    const standX = boardToCanvasX(s.standBoard);
    const targetX = boardToCanvasX(s.targetBoard);

    const distStand = Math.hypot(x - standX, y - FOUL_LINE_Y);
    const distTarget = Math.hypot(x - targetX, y - ARROWS_Y);

    if (distStand < 35 || Math.abs(y - FOUL_LINE_Y) < 30) {
      isDraggingStand = true;
      if (e.cancelable) e.preventDefault();
    } else if (distTarget < 35 || Math.abs(y - ARROWS_Y) < 30) {
      isDraggingTarget = true;
      if (e.cancelable) e.preventDefault();
    }
  }

  function handlePointerMove(e) {
    if (!isDraggingStand && !isDraggingTarget) return;

    const { x } = getCanvasCoords(e);
    const newBoard = canvasXToBoard(x);

    if (isDraggingStand) {
      appState.set({ standBoard: newBoard });
      if (standSlider) standSlider.value = newBoard;
      const el = document.getElementById('visStandVal');
      if (el) el.textContent = newBoard;
    } else if (isDraggingTarget) {
      appState.set({ targetBoard: newBoard });
      if (targetSlider) targetSlider.value = newBoard;
      const el = document.getElementById('visTargetVal');
      if (el) el.textContent = newBoard;
    }

    drawFrame();
    if (e.cancelable) e.preventDefault();
  }

  function handlePointerUp() {
    if (isDraggingStand || isDraggingTarget) {
      isDraggingStand = false;
      isDraggingTarget = false;
      runSimulation();
    }
  }

  canvas.addEventListener('mousedown', handlePointerDown);
  canvas.addEventListener('mousemove', handlePointerMove);
  canvas.addEventListener('mouseup', handlePointerUp);
  canvas.addEventListener('mouseleave', handlePointerUp);

  canvas.addEventListener('touchstart', handlePointerDown, { passive: false });
  canvas.addEventListener('touchmove', handlePointerMove, { passive: false });
  canvas.addEventListener('touchend', handlePointerUp);
  canvas.addEventListener('touchcancel', handlePointerUp);

  // Event Listeners for Oil Pattern Selector
  document.querySelectorAll('.oil-radio').forEach(label => {
    label.addEventListener('click', (e) => {
      e.preventDefault();
      const radio = label.querySelector('input');
      if (!radio) return;

      document.querySelectorAll('.oil-radio').forEach(l => {
        l.classList.remove('active');
        const r = l.querySelector('input');
        if (r) r.checked = false;
      });

      label.classList.add('active');
      radio.checked = true;
      appState.set({ activeOilCondition: radio.value });
      runSimulation();
      drawFrame();
    });
  });

  if (standSlider) {
    standSlider.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value);
      appState.set({ standBoard: val });
      document.getElementById('visStandVal').textContent = val;
      drawFrame();
    });
    standSlider.addEventListener('change', runSimulation);
  }

  if (targetSlider) {
    targetSlider.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value);
      appState.set({ targetBoard: val });
      document.getElementById('visTargetVal').textContent = val;
      drawFrame();
    });
    targetSlider.addEventListener('change', runSimulation);
  }

  if (speedSlider) {
    speedSlider.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value);
      appState.set({ speed: val });
      document.getElementById('visSpeedVal').textContent = val + ' MPH';
    });
    speedSlider.addEventListener('change', runSimulation);
  }

  if (revSlider) {
    revSlider.addEventListener('input', (e) => {
      const val = parseInt(e.target.value);
      appState.set({ revRate: val });
      document.getElementById('visRevVal').textContent = val + ' RPM';
    });
    revSlider.addEventListener('change', runSimulation);
  }

  if (drillingSelect) {
    drillingSelect.addEventListener('change', (e) => {
      appState.set({ drilling: e.target.value });
      runSimulation();
    });
  }

  if (ballSelect) {
    ballSelect.addEventListener('change', (e) => {
      appState.set({ activeBallId: e.target.value });
      runSimulation();
    });
  }

  if (compareToggle) {
    compareToggle.addEventListener('change', (e) => {
      const isChecked = e.target.checked;
      appState.set({ compareMode: isChecked });
      if (compareGroup) compareGroup.classList.toggle('hidden', !isChecked);
      runSimulation();
    });
  }

  if (ballSelect2) {
    ballSelect2.addEventListener('change', (e) => {
      appState.set({ compareBallId: e.target.value });
      runSimulation();
    });
  }

  if (btnRunSim) {
    btnRunSim.addEventListener('click', runSimulation);
  }

  // --- Exact BC Pro Shop Ball Reaction Physics Calculation ---
  function getFrictionCoeff(x, currentBoard, oilPattern, baseFriction, dryFriction) {
    let oilLengthX = 533; // 40ft
    let isSport = false;
    let isBurned = false;
    let isCarrydown = false;

    if (oilPattern === 'burned') { oilLengthX = 450; isBurned = true; } // 34ft
    else if (oilPattern === 'carrydown') { oilLengthX = 610; isCarrydown = true; } // 46ft
    else if (oilPattern === 'sport') { oilLengthX = 560; isSport = true; } // 42ft

    // Down-lane check
    if (x >= oilLengthX) {
      return isCarrydown ? dryFriction * 0.75 : dryFriction;
    }

    const distFromCenter = Math.abs(currentBoard - 20.5);

    if (isSport) {
      return baseFriction + (dryFriction - baseFriction) * 0.15;
    }

    if (isBurned) {
      if (distFromCenter >= 5 && distFromCenter <= 14) {
        return dryFriction * 0.85; // dry track area
      }
    }

    // Fresh / Standard House Pattern: oil inside (boards 10-30), dry outside (boards 1-9, 31-40)
    if (distFromCenter <= 7.5) {
      return baseFriction; // inside oil
    } else if (distFromCenter <= 12.5) {
      const t = (distFromCenter - 7.5) / 5;
      return baseFriction + t * (dryFriction * 0.6 - baseFriction);
    } else {
      return dryFriction; // outside dry boards
    }
  }

  function calculateTrajectory(ball, drilling, oilPattern, speed, revs) {
    const simPath = [];
    const laneY = 20;
    const laneH = 200;
    const foulX = 15;

    let x = foulX;
    const s = appState.get();

    const standBoard = s.standBoard;
    const targetBoard = s.targetBoard;

    let y = laneY + (laneH / 40) * (40 - standBoard);

    let vx = speed * 0.45;
    const yStand = laneY + (laneH / 40) * (40 - standBoard);
    const yTarget = laneY + (laneH / 40) * (40 - targetBoard);
    const dy = yTarget - yStand;
    const dx = 185;
    let vy = vx * (dy / dx);

    const rg = ball.rg || 2.48;
    const diff = ball.diff || 0.051;

    let baseFriction = 0.015;
    let dryFriction = 0.11;

    if (ball.coverType === 'Solid Reactive') {
      baseFriction = 0.024; dryFriction = 0.125;
    } else if (ball.coverType === 'Hybrid Reactive') {
      baseFriction = 0.020; dryFriction = 0.115;
    } else if (ball.coverType === 'Pearl Reactive') {
      baseFriction = 0.015; dryFriction = 0.110;
    } else if (ball.coverType === 'Urethane') {
      baseFriction = 0.040; dryFriction = 0.090;
    } else if (ball.coverType === 'Polyester') {
      baseFriction = 0.005; dryFriction = 0.020;
    }

    const isPolished = (ball.finish || '').toLowerCase().includes('polish');
    if (isPolished) {
      baseFriction *= 0.75; dryFriction *= 0.90;
    } else {
      baseFriction *= 1.25; dryFriction *= 1.10;
    }

    const revRatio = revs / 350;
    const baseSpin = vx * 0.70 * revRatio * (1 + (0.055 - diff) * 2);

    let ry = -baseSpin * 1.1;
    let frictionMultiplier = 1.0;

    if (drilling === 'pin-up') {
      ry = -baseSpin * 1.45; frictionMultiplier = 1.10;
    } else if (drilling === 'pin-down') {
      ry = -baseSpin * 0.72; frictionMultiplier = 0.85;
    } else if (drilling === 'max-flare') {
      ry = -baseSpin * 1.55; frictionMultiplier = 1.35;
    } else if (drilling === 'short-pin') {
      ry = -baseSpin * 0.35; frictionMultiplier = 0.50;
    }

    const dt = 0.08;
    let minBoardY = Math.round(40 - ((y - laneY) / laneH) * 40);
    let maxEntryAngle = 0;

    while (x < 740 && simPath.length < 130) {
      simPath.push({ x, y });

      const currentBoard = Math.round(40 - ((y - laneY) / laneH) * 40);
      if (currentBoard < minBoardY) minBoardY = currentBoard;

      const fCoeff = getFrictionCoeff(x, currentBoard, oilPattern, baseFriction, dryFriction) * frictionMultiplier;

      const sidewayDiff = ry - vy;
      vy += sidewayDiff * fCoeff * dt;

      x += vx;
      y += vy;

      if (x > 660) {
        const angle = Math.abs(Math.atan2(vy, vx) * (180 / Math.PI));
        if (angle > maxEntryAngle) maxEntryAngle = angle;
      }
    }

    return {
      path: simPath,
      breakpointBoard: minBoardY,
      entryAngle: Math.max(3.5, maxEntryAngle || 6.4)
    };
  }

  // --- Frame Rendering Routines ---
  function runSimulation() {
    if (simIsRunning) clearInterval(simInterval);

    const s = appState.get();
    const ball1 = s.arsenal.find(b => b.id === s.activeBallId) || s.arsenal[0];
    if (!ball1) return;

    activePins = DEFAULT_PINS.map(p => ({
      ...p,
      cx: boardToCanvasX(p.board),
      cy: PIN_DECK_Y + (63 - p.dist) * 12,
      hit: false, vx: 0, vy: 0
    }));

    const res1 = calculateTrajectory(ball1, s.drilling, s.activeOilCondition, s.speed, s.revRate);
    simPath1 = res1.path;

    if (s.compareMode) {
      const ball2 = s.arsenal.find(b => b.id === s.compareBallId) || s.arsenal[1] || ball1;
      const res2 = calculateTrajectory(ball2, s.drilling, s.activeOilCondition, s.speed, s.revRate);
      simPath2 = res2.path;
    } else {
      simPath2 = [];
    }

    const entryEl = document.getElementById('visEntryAngle');
    const breakEl = document.getElementById('visBreakpoint');
    if (entryEl) entryEl.textContent = res1.entryAngle.toFixed(1) + '°';
    if (breakEl) breakEl.textContent = 'Board ' + res1.breakpointBoard.toFixed(1);

    simIsRunning = true;
    currentFrame = 0;
    const maxFrames = Math.max(simPath1.length, simPath2.length);

    simInterval = setInterval(() => {
      if (currentFrame >= maxFrames) {
        clearInterval(simInterval);
        simIsRunning = false;
        return;
      }
      drawFrame();
      currentFrame++;
    }, 20);
  }

  function drawFrame() {
    if (!canvas.width || !canvas.height) return;

    ctx.save();
    ctx.scale(canvas.width / CANV_W, canvas.height / CANV_H);

    ctx.clearRect(0, 0, CANV_W, CANV_H);

    // 1. Lane Wood Background
    ctx.fillStyle = '#111823';
    ctx.fillRect(0, 0, CANV_W, CANV_H);

    // Gutters
    ctx.fillStyle = '#070a0e';
    ctx.fillRect(0, 0, GUTTER_MARGIN, CANV_H);
    ctx.fillRect(CANV_W - GUTTER_MARGIN, 0, GUTTER_MARGIN, CANV_H);

    // Board Lines
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.04)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 40; i++) {
      const bx = boardToCanvasX(i);
      ctx.beginPath();
      ctx.moveTo(bx, 0);
      ctx.lineTo(bx, CANV_H);
      ctx.stroke();
    }

    // Foul Line at Bottom
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(GUTTER_MARGIN, FOUL_LINE_Y);
    ctx.lineTo(CANV_W - GUTTER_MARGIN, FOUL_LINE_Y);
    ctx.stroke();

    // Arrows at Y = ARROWS_Y
    [5, 10, 15, 20, 25, 30, 35].forEach(board => {
      const bx = boardToCanvasX(board);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.beginPath();
      ctx.moveTo(bx, ARROWS_Y);
      ctx.lineTo(bx - 4, ARROWS_Y + 12);
      ctx.lineTo(bx + 4, ARROWS_Y + 12);
      ctx.closePath();
      ctx.fill();
    });

    // 2. Draw Oil Pattern Overlay
    drawOilOverlay(appState.get().activeOilCondition);

    // 3. Stand & Target Drag Markers
    const s = appState.get();
    const xStand = boardToCanvasX(s.standBoard);
    const xTarget = boardToCanvasX(s.targetBoard);

    // Projected targeting line (Dashed crimson)
    ctx.strokeStyle = 'rgba(255, 23, 68, 0.4)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(xStand, FOUL_LINE_Y);
    ctx.lineTo(xTarget, ARROWS_Y);
    ctx.stroke();
    ctx.setLineDash([]);

    // Stand Drag Handle (Red) at Foul Line
    ctx.fillStyle = '#ff1744';
    ctx.shadowColor = '#ff1744';
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.arc(xStand, FOUL_LINE_Y, 8, 0, Math.PI * 2);
    ctx.fill();

    ctx.font = 'bold 10px sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(`S:${s.standBoard}`, xStand - 12, FOUL_LINE_Y + 18);

    // Target Drag Handle (Amber) at Arrows
    ctx.fillStyle = '#ffb300';
    ctx.shadowColor = '#ffb300';
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.arc(xTarget, ARROWS_Y, 8, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.fillText(`T:${s.targetBoard}`, xTarget - 12, ARROWS_Y - 12);
    ctx.shadowBlur = 0;

    // 4. Trajectory Trails & Rolling Balls
    if (simPath1.length > 0) {
      ctx.strokeStyle = 'rgba(0, 242, 254, 0.8)';
      ctx.lineWidth = 3;
      ctx.beginPath();

      const pt0 = simPath1[0];
      const startX = boardToCanvasX(40 - ((pt0.y - 20) / 200) * 40);
      const startY = distanceToCanvasY(pt0.x);
      ctx.moveTo(startX, startY);

      const limit1 = Math.min(currentFrame, simPath1.length - 1);
      for (let i = 0; i <= limit1; i++) {
        const pt = simPath1[i];
        const cx = boardToCanvasX(40 - ((pt.y - 20) / 200) * 40);
        const cy = distanceToCanvasY(pt.x);
        ctx.lineTo(cx, cy);
      }
      ctx.stroke();

      const lastPt = simPath1[limit1];
      const ball1X = boardToCanvasX(40 - ((lastPt.y - 20) / 200) * 40);
      const ball1Y = distanceToCanvasY(lastPt.x);
      drawRollingBall(ball1X, ball1Y, '#00f2fe');

      if (simIsRunning) animatePins(ball1X, ball1Y);
    }

    if (s.compareMode && simPath2.length > 0) {
      ctx.strokeStyle = 'rgba(255, 183, 3, 0.8)';
      ctx.lineWidth = 3;
      ctx.beginPath();

      const pt0 = simPath2[0];
      const startX = boardToCanvasX(40 - ((pt0.y - 20) / 200) * 40);
      const startY = distanceToCanvasY(pt0.x);
      ctx.moveTo(startX, startY);

      const limit2 = Math.min(currentFrame, simPath2.length - 1);
      for (let i = 0; i <= limit2; i++) {
        const pt = simPath2[i];
        const cx = boardToCanvasX(40 - ((pt.y - 20) / 200) * 40);
        const cy = distanceToCanvasY(pt.x);
        ctx.lineTo(cx, cy);
      }
      ctx.stroke();

      const lastPt2 = simPath2[limit2];
      const ball2X = boardToCanvasX(40 - ((lastPt2.y - 20) / 200) * 40);
      const ball2Y = distanceToCanvasY(lastPt2.x);
      drawRollingBall(ball2X, ball2Y, '#ffb703');
    }

    // 5. Draw Pins
    drawPins();

    ctx.restore();
  }

  function drawOilOverlay(patternType) {
    let oilTopY = 220; // 40ft
    let maxOpacity = 0.55;
    let isFlat = false;

    if (patternType === 'burned') { oilTopY = 280; maxOpacity = 0.35; }
    else if (patternType === 'carrydown') { oilTopY = 170; maxOpacity = 0.6; }
    else if (patternType === 'sport') { oilTopY = 200; maxOpacity = 0.5; isFlat = true; }

    const oilH = FOUL_LINE_Y - oilTopY;
    const steps = 30;
    const segH = oilH / steps;

    for (let i = 0; i < steps; i++) {
      const yStart = FOUL_LINE_Y - (i + 1) * segH;
      const vertFactor = 1.0 - (i / steps);

      const grad = ctx.createLinearGradient(GUTTER_MARGIN, 0, CANV_W - GUTTER_MARGIN, 0);
      const op = maxOpacity * vertFactor;

      if (isFlat) {
        grad.addColorStop(0, `rgba(30, 80, 180, ${op * 0.4})`);
        grad.addColorStop(0.5, `rgba(30, 80, 180, ${op})`);
        grad.addColorStop(1, `rgba(30, 80, 180, ${op * 0.4})`);
      } else {
        grad.addColorStop(0, `rgba(30, 80, 180, 0.0)`);
        grad.addColorStop(0.35, `rgba(30, 80, 180, ${op})`);
        grad.addColorStop(0.5, `rgba(30, 80, 180, ${op * 1.2})`);
        grad.addColorStop(0.65, `rgba(30, 80, 180, ${op})`);
        grad.addColorStop(1, `rgba(30, 80, 180, 0.0)`);
      }

      ctx.fillStyle = grad;
      ctx.fillRect(GUTTER_MARGIN, yStart, LANE_WIDTH, segH + 0.5);
    }
  }

  function drawRollingBall(x, y, color) {
    if (!isFinite(x) || !isFinite(y)) return;
    try {
      ctx.save();
      // Shadow
      ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
      ctx.shadowBlur = 10;
      ctx.shadowOffsetY = 3;

      // 3D Ball Radial Gradient
      const radGrad = ctx.createRadialGradient(x - 4, y - 4, 2, x, y, 13);
      radGrad.addColorStop(0, '#ffffff');
      radGrad.addColorStop(0.35, color);
      radGrad.addColorStop(1, '#070a0e');

      ctx.fillStyle = radGrad;
      ctx.beginPath();
      ctx.arc(x, y, 13.5, 0, Math.PI * 2);
      ctx.fill();

      // Finger holes
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#0f172a';
      ctx.beginPath();
      ctx.arc(x - 3, y - 2, 1.8, 0, Math.PI * 2);
      ctx.arc(x + 3, y - 2, 1.8, 0, Math.PI * 2);
      ctx.arc(x, y + 4, 2.2, 0, Math.PI * 2);
      ctx.fill();
    } catch (e) {
      console.warn('Canvas radial gradient error caught:', e);
    } finally {
      ctx.restore();
    }
  }

  function animatePins(bx, by) {
    if (!isFinite(bx) || !isFinite(by)) return;
    activePins.forEach(pin => {
      // Hit radius: ball radius (13.5) + pin radius (9.5) = 23px threshold
      if (!pin.hit && Math.hypot(bx - pin.cx, by - pin.cy) < 23) {
        pin.hit = true;
        pin.vx = (pin.cx - bx) * 0.45 + (Math.random() - 0.5) * 5;
        pin.vy = -7 - Math.random() * 7;
      }
      if (pin.hit) {
        pin.cx += pin.vx;
        pin.cy += pin.vy;
        pin.vx *= 0.90;
        pin.vy *= 0.90;
      }
    });
  }

  function drawPins() {
    activePins.forEach(pin => {
      if (!pin || !isFinite(pin.cx) || !isFinite(pin.cy)) return;
      try {
        ctx.save();
        if (pin.hit) {
          ctx.fillStyle = 'rgba(200, 200, 200, 0.2)';
          ctx.strokeStyle = 'rgba(100, 116, 139, 0.3)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(pin.cx, pin.cy, 9, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        } else {
          // Pin Base Shadow
          ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
          ctx.shadowBlur = 6;
          ctx.shadowOffsetY = 2;

          // Pin 3D Radial Gradient
          const pGrad = ctx.createRadialGradient(pin.cx - 2, pin.cy - 3, 1, pin.cx, pin.cy, 9.5);
          pGrad.addColorStop(0, '#ffffff');
          pGrad.addColorStop(0.7, '#f1f5f9');
          pGrad.addColorStop(1, '#94a3b8');

          ctx.fillStyle = pGrad;
          ctx.strokeStyle = '#334155';
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.arc(pin.cx, pin.cy, 9.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();

          ctx.shadowBlur = 0;

          // Red Neck Stripe Ring
          ctx.fillStyle = '#ef4444';
          ctx.beginPath();
          ctx.arc(pin.cx, pin.cy, 3.5, 0, Math.PI * 2);
          ctx.fill();

          // Inner White Cap
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(pin.cx, pin.cy, 1.5, 0, Math.PI * 2);
          ctx.fill();
        }
      } catch (err) {
        console.warn('Pin radial gradient error caught:', err);
      } finally {
        ctx.restore();
      }
    });
  }

  updateControlsFromState();
  resizeCanvas();
  runSimulation();

  appState.subscribe(() => {
    updateControlsFromState();
    drawFrame();
  });
}
